'use client'

import Image from 'next/image'
import { useEffect, useMemo, useRef, useState, type ReactNode, type SyntheticEvent } from 'react'

import { cn } from '@/utilities/ui'
import { getOptimizedImageProps } from '@inoo-ch/payload-image-optimizer/frontend'
import type { Media as MediaType } from '@/payload-types'

export interface LightboxImageSlideProps {
  src: string
  alt: string
  /** Optional Payload Media resource. When provided, the image-optimizer's
   * variant-aware loader is wired into next/image so the browser fetches the
   * closest pre-baked size variant directly from storage instead of hitting
   * `/api/media/file/...` + `/_next/image`. */
  resource?: MediaType | null
  /** Intrinsic image width (used for aspect ratio). Falls back to rect width. */
  intrinsicWidth?: number
  /** Intrinsic image height (used for aspect ratio). Falls back to rect height. */
  intrinsicHeight?: number
  /** Viewport rect from yet-another-react-lightbox `render.slide`. */
  rect?: { width: number; height: number }
  /** ThumbHash / blur data URL rendered as a crossfading placeholder. */
  blurDataURL?: string
  /** Inner padding reserved around the image within `rect`. Default 64. */
  padding?: number
  sizes?: string
  imageClassName?: string
  /** Content rendered above the image (e.g. overlay buttons). */
  overlay?: ReactNode
  /** Content rendered directly below the image (e.g. project link). Reserves vertical space so the image doesn't overlap. */
  belowImage?: ReactNode
  /** Reserved vertical space in pixels for `belowImage`. Includes the gap between image and content. Default 80. */
  belowImageReservedHeight?: number
}

const TRANSITION = 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)'

function computeFitSize(
  intrinsicWidth: number | undefined,
  intrinsicHeight: number | undefined,
  rect: { width: number; height: number } | undefined,
  padding: number,
  reservedHeight: number,
): { width: number; height: number } {
  const viewportWidth = rect?.width ?? 0
  const viewportHeight = Math.max((rect?.height ?? 0) - reservedHeight, 0)
  const maxWidth = Math.max(viewportWidth - padding, 1)
  const maxHeight = Math.max(viewportHeight - padding, 1)
  const baseWidth = intrinsicWidth && intrinsicWidth > 0 ? intrinsicWidth : maxWidth
  const baseHeight = intrinsicHeight && intrinsicHeight > 0 ? intrinsicHeight : maxHeight
  const scale = Math.min(1, maxWidth / baseWidth, maxHeight / baseHeight)
  const appliedScale = Number.isFinite(scale) && scale > 0 ? scale : 1
  return {
    width: Math.round(baseWidth * appliedScale),
    height: Math.round(baseHeight * appliedScale),
  }
}

export default function LightboxImageSlide({
  src,
  alt,
  resource,
  intrinsicWidth,
  intrinsicHeight,
  rect,
  blurDataURL,
  padding = 64,
  sizes = '100vw',
  imageClassName,
  overlay,
  belowImage,
  belowImageReservedHeight = 80,
}: LightboxImageSlideProps) {
  const reservedHeight = belowImage ? belowImageReservedHeight : 0
  const [imageLoaded, setImageLoaded] = useState(false)
  const lastLoadedSrcRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // When a Media resource is supplied, the image-optimizer plugin gives us a
  // variant-aware loader that picks the closest pre-baked size URL (direct
  // from blob storage). Without this, next/image would fetch the top-level
  // `url` which is the `/api/media/file/...` Payload proxy.
  const optimized = useMemo(
    () => (resource ? getOptimizedImageProps(resource as any) : null),
    [resource],
  )

  const effectiveBlur = blurDataURL ?? optimized?.blurDataURL

  // Reset loaded state when src changes — mirrors ImageMedia's stability ref
  // so quick re-renders with the same src don't flash the placeholder back in.
  useEffect(() => {
    if (lastLoadedSrcRef.current === src) return
    lastLoadedSrcRef.current = src
    setImageLoaded(false)
  }, [src])

  // Cached-image fallback: onLoad can fire synchronously during DOM insertion,
  // before the reset effect above runs. Re-check after React settles.
  useEffect(() => {
    if (imageLoaded) return
    const img = containerRef.current?.querySelector('img')
    if (img?.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setImageLoaded(true)
    }
  }, [imageLoaded, src])

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setImageLoaded(true)
    }
  }

  const { width, height } = computeFitSize(
    intrinsicWidth,
    intrinsicHeight,
    rect,
    padding,
    reservedHeight,
  )

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-4">
      <div ref={containerRef} className="relative" style={{ width, height }}>
        {effectiveBlur && (
          <div
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
              backgroundImage: `url(${effectiveBlur})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              transition: TRANSITION,
              opacity: imageLoaded ? 0 : 1,
              ...(!imageLoaded ? { willChange: 'opacity' as const } : {}),
            }}
            aria-hidden="true"
          />
        )}
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={cn('object-contain relative z-10', imageClassName)}
          draggable={false}
          sizes={sizes}
          quality={75}
          {...(optimized?.loader ? { loader: optimized.loader } : {})}
          onLoad={handleLoad}
          style={{
            transition: TRANSITION,
            opacity: imageLoaded ? 1 : 0,
            ...(!imageLoaded ? { willChange: 'opacity' as const } : {}),
          }}
        />
        {overlay}
      </div>
      {belowImage && (
        <div className="pointer-events-none relative z-10 flex w-full justify-center px-4">
          <div className="pointer-events-auto">{belowImage}</div>
        </div>
      )}
    </div>
  )
}
